import { T } from '../domaine/libelles'
import { el, requis, vider } from './dom'

/*
  Panneau bas générique : choix d'espèce, choix de secteur, confirmations.

  Deux touches au maximum pour aboutir. Les options font 72 px de haut,
  alignées à gauche, atteignables au pouce.
*/

export interface OptionFeuille {
  cle: string
  titre: string
  /** Ligne secondaire, en mono. Ex. le nom latin de l'espèce. */
  sousTitre?: string
}

export interface ConfigFeuille {
  titre: string
  aide?: string
  options?: OptionFeuille[]
  /** Contenu libre, inséré avant les options. */
  contenu?: HTMLElement
  actionPrincipale?: { texte: string; surClic: () => void }
  texteAnnuler?: string
  surChoix?: (cle: string) => void
  surAnnuler?: () => void
}

let fermerCourante: (() => void) | null = null

export function feuilleOuverte(): boolean {
  return fermerCourante !== null
}

export function fermerFeuille(): void {
  fermerCourante?.()
}

export function ouvrirFeuille(config: ConfigFeuille): void {
  fermerFeuille()

  const noeud = requis('feuille')
  const focusPrecedent = document.activeElement as HTMLElement | null

  const panneau = el('div', { class: 'feuille__panneau' })
  panneau.append(el('h2', { class: 'feuille__titre', text: config.titre }))
  if (config.aide) {
    panneau.append(el('p', { class: 'feuille__aide', text: config.aide }))
  }
  if (config.contenu) panneau.append(config.contenu)

  const liste = el('div', { class: 'feuille__options' })

  for (const option of config.options ?? []) {
    const bouton = el('button', { type: 'button', class: 'option' }, [
      el('span', { class: 'option__titre', text: option.titre }),
      option.sousTitre ? el('span', { class: 'option__sous', text: option.sousTitre }) : null,
    ])
    bouton.addEventListener('click', () => {
      fermer()
      config.surChoix?.(option.cle)
    })
    liste.append(bouton)
  }

  if (config.actionPrincipale) {
    const bouton = el('button', {
      type: 'button',
      class: 'bouton-plein',
      text: config.actionPrincipale.texte,
    })
    bouton.addEventListener('click', () => {
      const action = config.actionPrincipale
      fermer()
      action?.surClic()
    })
    liste.append(bouton)
  }

  const annuler = el('button', {
    type: 'button',
    class: 'feuille__annuler',
    text: config.texteAnnuler ?? T.annuler,
  })
  annuler.addEventListener('click', () => {
    fermer()
    config.surAnnuler?.()
  })
  liste.append(annuler)

  panneau.append(liste)

  vider(noeud)
  noeud.append(panneau)
  noeud.setAttribute('aria-label', config.titre)
  noeud.hidden = false

  // Un appui sur le voile annule. Les appuis dans le panneau ne remontent pas.
  const surVoile = (e: MouseEvent) => {
    if (e.target === noeud) {
      fermer()
      config.surAnnuler?.()
    }
  }
  const surTouche = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      fermer()
      config.surAnnuler?.()
      return
    }
    if (e.key !== 'Tab') return
    // Piège à focus : le clavier ne doit pas sortir du panneau ouvert.
    const focusables = panneau.querySelectorAll<HTMLElement>(
      'button, input, textarea, select, [tabindex]:not([tabindex="-1"])',
    )
    if (focusables.length === 0) return
    const premier = focusables[0] as HTMLElement
    const dernier = focusables[focusables.length - 1] as HTMLElement
    if (e.shiftKey && document.activeElement === premier) {
      e.preventDefault()
      dernier.focus()
    } else if (!e.shiftKey && document.activeElement === dernier) {
      e.preventDefault()
      premier.focus()
    }
  }

  noeud.addEventListener('click', surVoile)
  document.addEventListener('keydown', surTouche)

  panneau.querySelector<HTMLElement>('input, button')?.focus()

  function fermer(): void {
    noeud.removeEventListener('click', surVoile)
    document.removeEventListener('keydown', surTouche)
    noeud.hidden = true
    vider(noeud)
    fermerCourante = null
    focusPrecedent?.focus?.()
  }

  fermerCourante = fermer
}
